import type { HuongTangTruong, TangTruong7Ngay, TheoTangTruong } from '@qlk/shared';
import { NGUONG_TANG_TRUONG_7_NGAY, SO_DONG_TANG_TRUONG } from '@qlk/shared';
import { Link } from 'react-router-dom';

import {
  DAU_DELTA,
  DanhSachSkeleton,
  EmptyState,
  Nut,
  SegmentedControl,
  chuCaiDau,
  gonSo,
  huongDelta,
  phanTramKhongDau,
  soNguyen,
} from '../../../ui/index.js';
import {
  KHUNG_DUONG_14,
  MAU_HANG,
  duong14,
  khoangGon,
  mauChuHang,
  moTaDelta,
  nhanDuong14,
} from './tangTruong.js';

/**
 * "Tăng trưởng lượt xem · 7 ngày" — năm kênh đổi lượt xem nhiều nhất trong
 * tuần, theo demo đã duyệt (29/10/2026, xem `docs/ui.md` mục 18).
 *
 * Khung này thay khối "Lượt xem toàn hệ thống". Khối cũ vẽ **một** đường cho cả
 * hệ thống: nó nói "tuần này cả nhà xem nhiều hơn 2,1%" mà không nói kênh nào,
 * nên không ai làm được gì sau khi đọc nó. Con số tổng vẫn còn — ở ô đầu tiên
 * của thanh chỉ số, cùng một hàm dựng (`components/luotXem.ts`).
 *
 * Khung **luôn** tính 7 ngày, không đổi theo ô chọn kỳ của trang: câu hỏi của
 * nó là "tuần này kênh nào đổi nhiều nhất", và câu đó chỉ có một kỳ.
 */

const MUC_HUONG = [
  { giaTri: 'tang', nhan: 'Tăng mạnh nhất' },
  { giaTri: 'giam', nhan: 'Giảm mạnh nhất' },
];

const MUC_THEO = [
  { giaTri: 'pct', nhan: 'theo %' },
  { giaTri: 'luot', nhan: 'theo lượt xem' },
];

export interface KhungTangTruongProps {
  duLieu: TangTruong7Ngay | undefined;
  dangTai: boolean;
  loi?: string | null;
  onThuLai?: () => void;
  huong: HuongTangTruong;
  theo: TheoTangTruong;
  onHuong: (h: HuongTangTruong) => void;
  onTheo: (t: TheoTangTruong) => void;
  /**
   * Người đang xem bị giới hạn kênh — phụ đề thêm "Kênh bạn quản lý ·".
   *
   * API đã lọc phạm vi, nhãn này chỉ **nói ra** điều đó: một bảng xếp hạng
   * không ghi rổ của nó là một bảng dễ bị đọc thành của cả công ty.
   */
  chiKenhCuaToi?: boolean;
}

export function KhungTangTruong({
  duLieu,
  dangTai,
  loi,
  onThuLai,
  huong,
  theo,
  onHuong,
  onTheo,
  chiKenhCuaToi = false,
}: KhungTangTruongProps) {
  const dong = duLieu?.items ?? [];
  const khoang = khoangGon(duLieu?.tu ?? '', duLieu?.den ?? '');
  const coLoi = loi !== null && loi !== undefined;

  return (
    <section
      data-khoi="tang-truong"
      className="flex min-w-0 flex-col rounded-the border border-vien bg-be-mat"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-vien-trong px-5 py-3.5">
        <div className="min-w-0">
          <h2 className="text-tieu-de-khoi font-semibold text-chu">
            Tăng trưởng lượt xem · 7 ngày
          </h2>
          <p className="mt-0.5 text-phu text-chu-phu">
            {chiKenhCuaToi ? 'Kênh bạn quản lý · ' : ''}
            so với tuần trước
            {khoang === '' ? '' : ` · ${khoang}`}
          </p>
        </div>

        {/* Hai nút chuyển: chiều, rồi thước đo. Thứ tự đó là thứ tự câu hỏi. */}
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            muc={MUC_HUONG}
            giaTri={huong}
            onChange={(g) => {
              onHuong(g as HuongTangTruong);
            }}
            aria-label="Chiều tăng trưởng"
          />
          <SegmentedControl
            muc={MUC_THEO}
            giaTri={theo}
            onChange={(g) => {
              onTheo(g as TheoTangTruong);
            }}
            aria-label="Thước đo tăng trưởng"
          />
        </div>
      </div>

      {dangTai ? (
        <div className="p-5">
          <DanhSachSkeleton so={SO_DONG_TANG_TRUONG} cao="2.5rem" />
        </div>
      ) : coLoi ? (
        <EmptyState
          loi
          title="Không tải được tăng trưởng lượt xem"
          description={loi}
          {...(onThuLai
            ? {
                action: (
                  <Nut kieu="phu" onClick={onThuLai}>
                    Thử lại
                  </Nut>
                ),
              }
            : {})}
        />
      ) : dong.length === 0 ? (
        /* Một dòng, không phải một khung rỗng to: tuần không có kênh nào đạt
           ngưỡng là chuyện thường, nó không đáng chiếm nửa trang. */
        <p className="px-5 py-4 text-co-ban text-chu-phu">Chưa có kênh nào đủ điều kiện</p>
      ) : (
        <ul className="flex flex-col">
          {dong.map((d, i) => {
            const net = duong14(d.chuoi14);
            const huongSo = huongDelta(d.delta);
            const mauNet = d.delta >= 0 ? 'var(--color-tang)' : 'var(--color-giam)';

            return (
              <li
                key={d.channelId}
                data-dong-tang-truong={d.channelId}
                className="border-b border-vien-trong last:border-b-0"
              >
                {/*
                  Cả dòng là một `Link`: vùng bấm phải bằng vùng trông như bấm
                  được (docs/ui.md mục 1). `?ky=7d` để trang kênh mở đúng kỳ mà
                  con số ở đây nói về — sang đó rồi thấy "28 ngày qua" là hai
                  trang nói hai chuyện.
                */}
                <Link
                  to={`/kenh/${d.channelId}?ky=7d`}
                  className="flex min-h-11 flex-wrap items-center gap-x-3 gap-y-2 px-5 py-3 transition-colors hover:bg-header-bang"
                >
                  <span className="so-lieu w-4 shrink-0 text-right text-phu font-semibold text-chu-phu">
                    {i + 1}
                  </span>

                  <span
                    className="inline-flex size-7 shrink-0 items-center justify-center rounded-o text-phu font-semibold"
                    style={{
                      backgroundColor: MAU_HANG[i] ?? MAU_HANG[MAU_HANG.length - 1],
                      color: mauChuHang(i + 1),
                    }}
                    aria-hidden="true"
                  >
                    {chuCaiDau(d.ten)}
                  </span>

                  <span className="min-w-0 flex-1 basis-40">
                    <span className="block truncate font-semibold text-chu">{d.ten}</span>
                    <span className="block truncate text-phu text-chu-phu">
                      {d.network ?? 'AdSense trực tiếp'}
                    </span>
                  </span>

                  <span className="shrink-0 text-right">
                    <span className="so-lieu block font-semibold text-chu">{gonSo(d.luot7)}</span>
                    <span className="so-lieu block text-phu text-chu-phu">
                      tuần trước {gonSo(d.luotTruoc)}
                    </span>
                  </span>

                  <svg
                    viewBox={`0 0 ${String(KHUNG_DUONG_14.rong)} ${String(KHUNG_DUONG_14.cao)}`}
                    preserveAspectRatio="none"
                    className="h-9 w-[72px] shrink-0"
                    role="img"
                    aria-label={nhanDuong14(d)}
                  >
                    <path
                      d={net.truoc}
                      fill="none"
                      stroke="var(--color-do-trong)"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                    <path
                      d={net.nay}
                      fill="none"
                      stroke={mauNet}
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      vectorEffect="non-scaling-stroke"
                    />
                  </svg>

                  <span className="shrink-0 text-right">
                    <span
                      className={`so-lieu block text-phu font-semibold ${huongSo === 'tang' ? 'text-tang' : huongSo === 'giam' ? 'text-giam' : 'text-chu-phu'}`}
                    >
                      {DAU_DELTA[huongSo]} {phanTramKhongDau(d.pct)}
                    </span>
                    <span className="so-lieu block text-phu text-chu-phu">
                      {moTaDelta(d.delta)}
                    </span>
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}

      {!dangTai && !coLoi && dong.length > 0 && (
        <div className="flex flex-wrap items-center justify-between gap-2 border-t border-vien-trong px-5 py-3">
          <p className="text-phu text-chu-phu">
            Xám: tuần trước · màu: 7 ngày qua ({khoang}) · chỉ xếp hạng kênh có ≥{' '}
            {soNguyen(NGUONG_TANG_TRUONG_7_NGAY)} lượt xem tuần trước
          </p>
          <Link to="/phan-tich" className="text-phu font-semibold text-nhan hover:text-nhan-dam">
            Xem đủ ở Hiệu suất →
          </Link>
        </div>
      )}
    </section>
  );
}
